import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { searchSatProductsOrServices, SatProductOrServiceOption } from 'utils/api/baas/invoicing/satCatalog';

const DEBOUNCE_MS = 300;
/**
 * Facturama rejects keywords of 3 chars or fewer ("keyword mayor a 3 caracteres"),
 * so the real minimum is 4. Exported so the UI gates and copy stay in sync with the
 * actual provider limit instead of duplicating a magic number.
 */
export const SAT_SEARCH_MIN_CHARS = 4;
/** SAT catalog changes very rarely. Aligns with the 24h Redis TTL on the backend wrapper. */
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

interface UseSatProductKeySearchResult {
  options: SatProductOrServiceOption[];
  isLoading: boolean;
}

/**
 * Server-side search for SAT product/service keys with debounce.
 *
 * The search fires only after the user has typed at least SAT_SEARCH_MIN_CHARS characters
 * (Facturama rejects 3 or fewer) and 300 ms have passed since the last keystroke. Results
 * are kept in React Query for a day so re-searches for the same query are instantaneous.
 */
export function useSatProductKeySearch(query: string): UseSatProductKeySearchResult {
  const debouncedQuery = useDebouncedValue(query.trim(), DEBOUNCE_MS);
  const enabled = debouncedQuery.length >= SAT_SEARCH_MIN_CHARS;

  const { data, isFetching, isPreviousData } = useQuery<SatProductOrServiceOption[]>(
    ['sat-products-or-services', debouncedQuery],
    () => searchSatProductsOrServices(debouncedQuery),
    {
      enabled,
      staleTime: ONE_DAY_MS,
      cacheTime: ONE_DAY_MS,
      keepPreviousData: true,
    },
  );

  // keepPreviousData makes React Query return the prior query's data while the new one is
  // in flight (isPreviousData === true). That avoids an empty flicker mid-typing, but it
  // also flashes the previous result when the query jumps (e.g. pasting a new key). We treat
  // that in-flight window as "loading": clear the options and flag isLoading so the consumer
  // can show a "searching" state instead of stale results.
  const showingStaleWhileFetching = isPreviousData;

  return {
    // Empty options when: query too short to fire, or showing previous data while refetching.
    options: enabled && !showingStaleWhileFetching ? data ?? [] : [],
    isLoading: enabled && isFetching,
  };
}
